import { supabase } from '@/utils/supabase';

import logger from '@/utils/logger';
export interface PreApprovalTemplate {
  id?: string;
  organization_id?: string;
  created_by_user_id?: string;
  name: string;
  description?: string;
  is_organization_default?: boolean;
  is_corporate_default?: boolean;
  is_locked?: boolean;
  template_version?: number;
  last_updated_by?: string;
  sections: Record<string, unknown>[]; // The template elements/sections from VisualTemplateBuilder
  style_config?: Record<string, unknown>; // The template styles and configuration
  created_at?: string;
  updated_at?: string;
}

export interface TemplateListItem {
  id: string;
  name: string;
  description?: string;
  is_organization_default: boolean;
  created_at: string;
  updated_at: string;
  created_by_user_id: string;
}

class PreApprovalTemplateService {
  /**
   * Check if the current user has admin role
   */
  async isCurrentUserAdmin(): Promise<boolean> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        return false;
      }

      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('roles')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        return false;
      }

      return profile.roles?.includes('admin') ?? false;
    } catch (error) {
      logger.error('Error checking if user is admin:', error);
      return false;
    }
  }

  /**
   * Save a template to the database (create or update)
   */
  async saveTemplate(template: PreApprovalTemplate): Promise<{ data: PreApprovalTemplate | null; error: Error | null }> {
    try {
      // Get current user and organization from auth
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { data: null, error: new Error('User not authenticated') };
      }

      // Check if template is locked and user is not admin
      if (template.id && template.is_locked) {
        const isAdmin = await this.isCurrentUserAdmin();
        if (!isAdmin) {
          return { data: null, error: new Error('Only administrators can modify locked templates') };
        }
      }

      // Get user's organization
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        return { data: null, error: new Error('User organization not found') };
      }


      // Ensure visual templates have empty sections
      const isVisualTemplate = template.style_config && (template.style_config as any).elements;
      const sections = isVisualTemplate ? [] : (template.sections || []);
      
      // If setting corporate default, ensure only one exists
      if (template.is_corporate_default) {
        // Unset any existing corporate defaults
        const { error: unsetError } = await supabase
          .from('pre_approval_templates')
          .update({ is_corporate_default: false })
          .eq('is_corporate_default', true);
        
        if (unsetError) {
          logger.error('Error unsetting corporate defaults:', unsetError);
        }
      }

      const templateData = {
        name: template.name,
        description: template.description || '',
        sections,
        style_config: template.style_config || {},
        is_organization_default: template.is_organization_default || false,
        is_corporate_default: template.is_corporate_default || false,
        is_locked: template.is_locked || false,
        organization_id: profile.organization_id,
        created_by_user_id: user.id,
        updated_at: new Date().toISOString()
      };



      if (template.id) {
        // Update existing template
        const { data, error } = await supabase
          .from('pre_approval_templates')
          .update(templateData)
          .eq('id', template.id)
          .select()
          .single();

        if (error) {
          logger.error('Error updating template:', error);
          return { data: null, error };
        }

        // Check if the update actually found and updated a row
        if (!data) {
          logger.error('Template update failed: No matching template found or RLS policy blocked update');
          return { 
            data: null, 
            error: new Error('Unable to update template. You may not have permission to modify this template.') 
          };
        }


        return { data, error: null };
      } else {
        // Create new template
        const { data, error } = await supabase
          .from('pre_approval_templates')
          .insert({
            ...templateData,
            created_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) {
          logger.error('Error creating template:', error);
          return { data: null, error };
        }

        return { data, error: null };
      }
    } catch (error) {
      logger.error('Error in saveTemplate:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Load all templates for the current user's organization
   */
  async loadTemplates(): Promise<{ data: PreApprovalTemplate[] | null; error: Error | null }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { data: null, error: new Error('User not authenticated') };
      }

      // Get user's organization
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        return { data: null, error: new Error('User organization not found') };
      }

      // Load templates for the organization AND corporate defaults
      const { data, error } = await supabase
        .from('pre_approval_templates')
        .select('*')
        .or(`organization_id.eq.${profile.organization_id},is_corporate_default.eq.true`)
        .order('is_corporate_default', { ascending: false }) // Corporate defaults first
        .order('is_organization_default', { ascending: false }) // Then organization defaults
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error loading templates:', error);
        return { data: null, error };
      }

      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error in loadTemplates:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get a single template by ID
   */
  async getTemplateById(templateId: string): Promise<{ data: PreApprovalTemplate | null; error: Error | null }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { data: null, error: new Error('User not authenticated') };
      }

      // Get user's organization
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        return { data: null, error: new Error('User organization not found') };
      }

      const { data, error } = await supabase
        .from('pre_approval_templates')
        .select('*')
        .eq('id', templateId)
        .eq('organization_id', profile.organization_id)
        .single();

      if (error) {
        logger.error('Error getting template:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      logger.error('Error in getTemplateById:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId: string): Promise<{ error: Error | null }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { error: new Error('User not authenticated') };
      }

      // Get user's organization
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        return { error: new Error('User organization not found') };
      }

      const { error } = await supabase
        .from('pre_approval_templates')
        .delete()
        .eq('id', templateId)
        .eq('organization_id', profile.organization_id);

      if (error) {
        logger.error('Error deleting template:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      logger.error('Error in deleteTemplate:', error);
      return { error: error as Error };
    }
  }

  /**
   * Set a template as the organization default
   * @deprecated Consider using user-level preferences via userPreferencesService.updateWorkflowPreferences() instead
   */
  async setOrganizationDefaultTemplate(templateId: string): Promise<{ error: Error | null }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { error: new Error('User not authenticated') };
      }

      // Get user's organization and role
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id, roles')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        return { error: new Error('User organization not found') };
      }

      // Note: Removing admin check - all users should be able to set their preferred default template
      // Organization-wide defaults can still be controlled by admins through is_corporate_default

      // First, unset ALL existing organization defaults for this organization (but NOT corporate defaults)
      // This ensures only one organization default exists per organization
      const { error: unsetError } = await supabase
        .from('pre_approval_templates')
        .update({ is_organization_default: false })
        .eq('organization_id', profile.organization_id)
        .eq('is_organization_default', true)
        .neq('is_corporate_default', true); // Don't touch corporate defaults (use neq for safety)

      if (unsetError) {
        logger.error('Error unsetting default template:', unsetError);
        return { error: unsetError };
      }

      logger.info(`Unset existing organization defaults for organization: ${profile.organization_id}`);

      // Check if trying to set a corporate default as organization default
      const { data: templateCheck, error: checkError } = await supabase
        .from('pre_approval_templates')
        .select('is_corporate_default')
        .eq('id', templateId)
        .single();

      if (checkError) {
        return { error: checkError };
      }

      if (templateCheck?.is_corporate_default) {
        return { error: new Error('Cannot set corporate default as organization default. Corporate defaults are already available to all users.') };
      }

      // Set the new default
      const { error } = await supabase
        .from('pre_approval_templates')
        .update({ is_organization_default: true })
        .eq('id', templateId)
        .eq('organization_id', profile.organization_id);

      if (error) {
        logger.error('Error setting default template:', error);
        return { error };
      }

      logger.info(`Set template ${templateId} as organization default for organization: ${profile.organization_id}`);
      return { error: null };
    } catch (error) {
      logger.error('Error in setDefaultTemplate:', error);
      return { error: error as Error };
    }
  }

  /**
   * Unset the organization default template for the current user
   * @deprecated Consider using user-level preferences via userPreferencesService.updateWorkflowPreferences() instead
   */
  async unsetOrganizationDefaultTemplate(): Promise<{ error: Error | null }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { error: new Error('User not authenticated') };
      }

      // Get user's organization
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        return { error: new Error('User organization not found') };
      }

      // Unset organization default (but NOT corporate defaults)
      const { error } = await supabase
        .from('pre_approval_templates')
        .update({ is_organization_default: false })
        .eq('organization_id', profile.organization_id)
        .eq('is_organization_default', true)
        .neq('is_corporate_default', true); // Don't touch corporate defaults

      if (error) {
        logger.error('Error unsetting default template:', error);
        return { error };
      }

      logger.info(`Unset organization default for organization: ${profile.organization_id}`);
      return { error: null };
    } catch (error) {
      logger.error('Error in unsetOrganizationDefaultTemplate:', error);
      return { error: error as Error };
    }
  }

  /**
   * @deprecated Use setOrganizationDefaultTemplate() for clarity or userPreferencesService for user-level defaults
   */
  async setDefaultTemplate(templateId: string): Promise<{ error: Error | null }> {
    console.warn('DEPRECATED: setDefaultTemplate() is deprecated. Use setOrganizationDefaultTemplate() for organization-wide defaults or userPreferencesService.updateWorkflowPreferences() for user-level defaults.');
    return this.setOrganizationDefaultTemplate(templateId);
  }

  /**
   * @deprecated Use unsetOrganizationDefaultTemplate() for clarity or userPreferencesService for user-level defaults
   */
  async unsetDefaultTemplate(): Promise<{ error: Error | null }> {
    console.warn('DEPRECATED: unsetDefaultTemplate() is deprecated. Use unsetOrganizationDefaultTemplate() for organization-wide defaults or userPreferencesService.updateWorkflowPreferences() for user-level defaults.');
    return this.unsetOrganizationDefaultTemplate();
  }

  /**
   * Get the organization's default template
   */
  async getDefaultTemplate(): Promise<{ data: PreApprovalTemplate | null; error: Error | null }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { data: null, error: new Error('User not authenticated') };
      }

      // Get user's organization
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        return { data: null, error: new Error('User organization not found') };
      }

      const { data, error } = await supabase
        .from('pre_approval_templates')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .eq('is_organization_default', true)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        logger.error('Error getting default template:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      logger.error('Error in getDefaultTemplate:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Clone an existing template
   */
  async cloneTemplate(templateId: string, newName: string): Promise<{ data: PreApprovalTemplate | null; error: Error | null }> {
    try {
      // First get the template to clone
      const { data: originalTemplate, error: getError } = await this.getTemplateById(templateId);
      if (getError || !originalTemplate) {
        return { data: null, error: getError || new Error('Template not found') };
      }

      // Create a new template with the cloned data
      // For visual templates (those with style_config), ensure sections is empty
      const clonedTemplate: PreApprovalTemplate = {
        name: newName,
        description: originalTemplate.description ? `Copy of ${originalTemplate.description}` : `Copy of ${originalTemplate.name}`,
        sections: originalTemplate.style_config?.elements ? [] : originalTemplate.sections, // Visual templates don't use sections
        style_config: originalTemplate.style_config,
        is_organization_default: false // Cloned templates are never default
      };

      return await this.saveTemplate(clonedTemplate);
    } catch (error) {
      logger.error('Error in cloneTemplate:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get a list of templates with minimal data for display
   */
  async getTemplateList(): Promise<{ data: TemplateListItem[] | null; error: Error | null }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return { data: null, error: new Error('User not authenticated') };
      }

      // Get user's organization
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        return { data: null, error: new Error('User organization not found') };
      }

      const { data, error } = await supabase
        .from('pre_approval_templates')
        .select('id, name, description, is_organization_default, created_at, updated_at, created_by_user_id')
        .eq('organization_id', profile.organization_id)
        .order('is_organization_default', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error getting template list:', error);
        return { data: null, error };
      }

      return { data: data || [], error: null };
    } catch (error) {
      logger.error('Error in getTemplateList:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Check if the current user can edit a template
   */
  async canEditTemplate(templateId: string): Promise<boolean> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return false;
      }

      // Get user's profile
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id, roles')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        return false;
      }

      // Admins can edit any template (including locked ones)
      if (profile.roles?.includes('admin')) {
        return true;
      }

      // Get the template
      const { data: template, error: templateError } = await supabase
        .from('pre_approval_templates')
        .select('created_by_user_id, organization_id, is_locked')
        .eq('id', templateId)
        .single();

      if (templateError || !template) {
        return false;
      }

      // If template is locked, only admins can edit (checked above)
      if (template.is_locked) {
        return false;
      }

      // Check if user is in the same organization
      if (template.organization_id !== profile.organization_id) {
        return false;
      }

      // Regular users can only edit their own templates
      return template.created_by_user_id === user.id;
    } catch (error) {
      logger.error('Error in canEditTemplate:', error);
      return false;
    }
  }

  /**
   * Get the corporate default template
   */
  async getCorporateDefaultTemplate(): Promise<{ data: PreApprovalTemplate | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('pre_approval_templates')
        .select('*')
        .eq('is_corporate_default', true)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        logger.error('Error getting corporate default template:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      logger.error('Error in getCorporateDefaultTemplate:', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Duplicate a template (for cloning locked templates)
   */
  async duplicateTemplate(templateId: string, newName?: string): Promise<{ data: PreApprovalTemplate | null; error: Error | null }> {
    try {
      // Get the template to duplicate
      const { data: originalTemplate, error: getError } = await this.getTemplateById(templateId);
      if (getError || !originalTemplate) {
        return { data: null, error: getError || new Error('Template not found') };
      }

      // Create a new template with the duplicated data
      // For visual templates (those with style_config), ensure sections is empty
      const duplicatedTemplate: PreApprovalTemplate = {
        name: newName || `${originalTemplate.name} (Copy)`,
        description: originalTemplate.description ? `Copy of ${originalTemplate.description}` : `Copy of ${originalTemplate.name}`,
        sections: originalTemplate.style_config?.elements ? [] : originalTemplate.sections, // Visual templates don't use sections
        style_config: originalTemplate.style_config,
        is_organization_default: false,
        is_corporate_default: false,
        is_locked: false // Duplicated templates are never locked
      };

      return await this.saveTemplate(duplicatedTemplate);
    } catch (error) {
      logger.error('Error in duplicateTemplate:', error);
      return { data: null, error: error as Error };
    }
  }
}

// Export a singleton instance
export const preApprovalTemplateService = new PreApprovalTemplateService();